import React, { useEffect, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import MoneyBagIcon from './MoneyBagIcon';
import { castGivingVote, fetchCurrentGivingCycle } from '../../utils/givingApi';

const GivingModal = ({ isOpen, onClose, embedded = false }) => {
  const [giving, setGiving] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!isOpen || embedded) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, embedded]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    setIsLoading(true);
    setError('');
    setGiving(null);
    setSelectedId(null);

    fetchCurrentGivingCycle()
      .then((data) => {
        if (!active) return;
        setGiving(data);
        setSelectedId(data.userVoteId || null);
      })
      .catch((loadError) => {
        if (!active) return;
        if (loadError.status === 404) {
          setGiving({ cycle: null, userVoteId: null, leaderId: null, votingOpen: false });
          setSelectedId(null);
          return;
        }
        setError(loadError.message || 'Unable to load this month’s vote.');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => { active = false; };
  }, [isOpen, revision]);

  if (!isOpen) return null;

  const cycle = giving?.cycle;
  const votedId = giving?.userVoteId || null;
  const hasVoted = Boolean(votedId);
  const isUnchanged = hasVoted && selectedId === votedId;
  const votingOpen = giving?.votingOpen === true;

  const handleSubmit = async () => {
    if (!selectedId || isUnchanged || !votingOpen || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      const updated = await castGivingVote(selectedId);
      setGiving(updated);
      setSelectedId(updated.userVoteId || null);
    } catch (saveError) {
      setError(saveError.message || 'Unable to save your vote.');
    } finally {
      setIsSaving(false);
    }
  };

  const submitLabel = () => {
    if (!votingOpen) return 'Voting closed';
    if (isSaving) return 'Saving…';
    if (isUnchanged) return 'Vote submitted';
    if (hasVoted) return 'Change vote';
    return 'Cast vote';
  };

  return (
    <div
      className={embedded ? 'settings-giving-embedded' : 'fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4 backdrop-blur-[2px]'}
      onMouseDown={(event) => {
        if (!embedded && event.target === event.currentTarget) onClose?.();
      }}
      role={embedded ? undefined : 'presentation'}
    >
      <div
        className={embedded ? 'theme-panel-bg' : 'flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border theme-border theme-panel-bg shadow-2xl'}
        role={embedded ? undefined : 'dialog'}
        aria-modal={embedded ? undefined : true}
        aria-label={embedded ? undefined : "Monthly charity vote"}
      >
        <div className="flex items-start justify-between gap-3 border-b theme-border px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <MoneyBagIcon size={22} />
            </span>
            <div>
              {!embedded && <div className="font-semibold theme-text-primary">Giving</div>}
              <div className="text-xs theme-text-muted">
                {cycle?.pledgePercent ?? 10}% of Arrow&apos;s earnings go to a charity you choose.
              </div>
            </div>
          </div>
          {!embedded && (
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg theme-text-muted hover:bg-[var(--color-sidebar-item-hover)]"
              title="Close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <div className={embedded ? 'px-5 py-4' : 'min-h-0 flex-1 overflow-y-auto px-5 py-4'}>
          {isLoading && (
            <div role="status" className="flex items-center justify-center gap-2 py-12 text-sm theme-text-muted">
              <Loader2 size={17} className="animate-spin" />
              Loading this month&apos;s vote…
            </div>
          )}

          {!isLoading && !cycle && !error && (
            <div className="rounded-xl border theme-border px-4 py-8 text-center text-sm theme-text-muted">
              Charity voting coming soon
            </div>
          )}

          {!isLoading && cycle && (
            <>
              <div className="mb-2 text-xs font-medium theme-text-muted">{cycle.label} nominees</div>
              <div className="flex flex-col gap-2">
                {cycle.nominees.map((nominee) => {
                  const isSelected = selectedId === nominee.id;
                  const isVoted = votedId === nominee.id;
                  return (
                    <button
                      key={nominee.id}
                      type="button"
                      onClick={() => votingOpen && setSelectedId(nominee.id)}
                      disabled={!votingOpen}
                      aria-pressed={isSelected}
                      className={`w-full rounded-xl border px-3.5 py-3 text-left transition-colors disabled:cursor-default ${
                        isSelected
                          ? 'border-amber-500/60 bg-amber-500/[0.07]'
                          : 'theme-border theme-card-bg hover:bg-[var(--color-sidebar-item-hover)]'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border transition-colors ${
                            isSelected ? 'border-amber-500 bg-amber-500 text-white' : ''
                          }`}
                          style={isSelected ? undefined : { borderColor: 'color-mix(in srgb, var(--color-text-muted), transparent 50%)' }}
                          aria-hidden="true"
                        >
                          {isSelected && <Check size={11} strokeWidth={3} />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate text-sm font-semibold theme-text-primary">{nominee.name}</span>
                            {nominee.id === giving.leaderId && (
                              <span className="flex-shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">Leading</span>
                            )}
                            {isVoted && (
                              <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Your vote</span>
                            )}
                          </div>
                          {nominee.blurb && <div className="mt-0.5 line-clamp-2 text-xs theme-text-muted">{nominee.blurb}</div>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {error && <div role="alert" className="mt-3 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-400">{error}{!cycle && <button type="button" className="settings-button ml-3" onClick={() => setRevision(value => value + 1)}>Try again</button>}</div>}
        </div>

        {cycle && (
          <div className="flex items-center justify-between gap-3 border-t theme-border px-5 py-3">
            <span className="text-xs leading-tight theme-text-muted">One vote per member · change it anytime</span>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!selectedId || isUnchanged || !votingOpen || isSaving}
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUnchanged && <Check size={15} strokeWidth={3} />}
              {submitLabel()}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GivingModal;
